// Typed fetch helpers for every endpoint in specs/plan.md's "API" section.
// Box/artwork/member shapes below are the backend's confirmed, settled
// contract (internal/server/*.go) — no guessing. Auth mirrors lilac's
// email/password flow (internal/server/auth.go, ported wholesale).
import type {
	ArtDate,
	Artwork,
	ArtworkDetail,
	Asset,
	Box,
	BoxDetail,
	BoxSummary,
	Invite,
	Member,
	NewAssetInput,
	Role,
	SignResponse,
	User,
} from "./types"

async function requestJSON<T>(url: string, init?: RequestInit): Promise<T> {
	const res = await fetch(url, init)
	const data = await res.json().catch(() => null)
	if (!res.ok) {
		throw new Error((data && data.error) || `Request failed (${res.status})`)
	}
	return data as T
}

function jsonInit(method: string, body: unknown): RequestInit {
	return {
		method,
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	}
}

// Auth endpoints (signup/login/verify-email/reset-password) wrap the user as
// `{user: {...}}`, per lilac; the dev-login escape hatch returns the user
// bare. Accept either so both work without guessing which one the currently
// migrating backend settles on.
function unwrapUser(data: unknown): User {
	if (data && typeof data === "object" && "user" in data) {
		return (data as { user: User }).user
	}
	return data as User
}

export type LoginResult =
	| { kind: "verification_required"; email: string }
	| { kind: "authed" }

function toLoginResult(data: unknown): LoginResult {
	if (
		data &&
		typeof data === "object" &&
		(data as { verification_required?: boolean }).verification_required
	) {
		return {
			kind: "verification_required",
			email: (data as { email: string }).email,
		}
	}
	return { kind: "authed" }
}

export const api = {
	// GET /api/auth/me → {user: {...}} or 401.
	me(): Promise<User> {
		return requestJSON<unknown>("/api/auth/me").then(unwrapUser)
	},
	logout(): Promise<unknown> {
		return requestJSON("/api/auth/logout", { method: "POST" })
	},
	signup(email: string, password: string): Promise<LoginResult> {
		return requestJSON<unknown>(
			"/api/auth/signup",
			jsonInit("POST", { email, password }),
		).then(toLoginResult)
	},
	login(email: string, password: string): Promise<LoginResult> {
		return requestJSON<unknown>(
			"/api/auth/login",
			jsonInit("POST", { email, password }),
		).then(toLoginResult)
	},
	verifyEmail(email: string, code: string): Promise<User> {
		return requestJSON<unknown>(
			"/api/auth/verify-email",
			jsonInit("POST", { email, code }),
		).then(unwrapUser)
	},
	resendCode(email: string): Promise<unknown> {
		return requestJSON("/api/auth/resend-code", jsonInit("POST", { email }))
	},
	forgotPassword(email: string): Promise<unknown> {
		return requestJSON("/api/auth/forgot-password", jsonInit("POST", { email }))
	},
	resetPassword(email: string, code: string, password: string): Promise<User> {
		return requestJSON<unknown>(
			"/api/auth/reset-password",
			jsonInit("POST", { email, code, password }),
		).then(unwrapUser)
	},
	// Dev-only escape hatch (dev builds only; 404s in prod).
	devLogin(email: string, name: string): Promise<User> {
		return requestJSON<unknown>(
			"/api/dev/login",
			jsonInit("POST", { email, name }),
		).then(unwrapUser)
	},

	config(): Promise<{ cloud_name: string }> {
		return requestJSON("/api/config")
	},

	// GET /api/boxes → flat array of boxes-plus-role/count/cover.
	listBoxes(): Promise<BoxSummary[]> {
		return requestJSON<BoxSummary[]>("/api/boxes")
	},
	createBox(name: string): Promise<Box> {
		return requestJSON<Box>("/api/boxes", jsonInit("POST", { name }))
	},
	renameBox(id: number, name: string): Promise<Box> {
		return requestJSON<Box>(`/api/boxes/${id}`, jsonInit("PATCH", { name }))
	},
	deleteBox(id: number): Promise<unknown> {
		return requestJSON(`/api/boxes/${id}`, { method: "DELETE" })
	},
	getBox(id: number): Promise<BoxDetail> {
		return requestJSON<BoxDetail>(`/api/boxes/${id}`)
	},

	// GET /api/boxes/:id/artworks → flat array, date desc (undated last),
	// assets nested.
	listArtworks(boxId: number): Promise<Artwork[]> {
		return requestJSON<Artwork[]>(`/api/boxes/${boxId}/artworks`)
	},
	createArtwork(
		boxId: number,
		body: {
			title: string
			note: string
			date: ArtDate
			assets: NewAssetInput[]
		},
	): Promise<Artwork> {
		return requestJSON<Artwork>(
			`/api/boxes/${boxId}/artworks`,
			jsonInit("POST", body),
		)
	},
	getArtwork(id: number): Promise<ArtworkDetail> {
		return requestJSON<ArtworkDetail>(`/api/artworks/${id}`)
	},
	updateArtwork(
		id: number,
		body: Partial<{ title: string; note: string; date: ArtDate }>,
	): Promise<Artwork> {
		return requestJSON<Artwork>(`/api/artworks/${id}`, jsonInit("PATCH", body))
	},
	addAsset(artworkId: number, asset: NewAssetInput): Promise<Asset> {
		return requestJSON<Asset>(
			`/api/artworks/${artworkId}/assets`,
			jsonInit("POST", asset),
		)
	},
	deleteAsset(artworkId: number, assetId: number): Promise<unknown> {
		return requestJSON(`/api/artworks/${artworkId}/assets/${assetId}`, {
			method: "DELETE",
		})
	},
	deleteArtwork(id: number): Promise<unknown> {
		return requestJSON(`/api/artworks/${id}`, { method: "DELETE" })
	},

	// Recordings must say whether they carry pictures: both kinds upload
	// under the "video" resource type, but only real video gets downscaled
	// and gets a poster frame.
	signUpload(
		boxId: number,
		resourceType: "image" | "video",
		recordingKind?: "audio" | "video",
	): Promise<SignResponse> {
		return requestJSON<SignResponse>(
			`/api/boxes/${boxId}/uploads/sign`,
			jsonInit("POST", {
				resource_type: resourceType,
				recording_kind: recordingKind ?? "",
			}),
		)
	},

	destroyUpload(
		boxId: number,
		publicId: string,
		resourceType: "image" | "video",
	): Promise<unknown> {
		return requestJSON(
			`/api/boxes/${boxId}/uploads/destroy`,
			jsonInit("POST", { public_id: publicId, resource_type: resourceType }),
		)
	},

	// Member/invite mutations all return the box's updated {members, invites}.
	inviteMember(
		boxId: number,
		email: string,
		role: Role,
	): Promise<{ members: Member[]; invites: Invite[] }> {
		return requestJSON(
			`/api/boxes/${boxId}/invites`,
			jsonInit("POST", { email, role }),
		)
	},
	revokeInvite(
		boxId: number,
		inviteId: number,
	): Promise<{ members: Member[]; invites: Invite[] }> {
		return requestJSON(`/api/boxes/${boxId}/invites/${inviteId}`, {
			method: "DELETE",
		})
	},
	removeMember(
		boxId: number,
		userId: number,
	): Promise<{ members: Member[]; invites: Invite[] }> {
		return requestJSON(`/api/boxes/${boxId}/members/${userId}`, {
			method: "DELETE",
		})
	},
}
